import { gql, TypedDocumentNode } from "@apollo/client";
import type { EventWithType } from "@/pages/events/[eventSlug]/utils";
import {
  CHANGE_REQUEST_CHANGE_FRAGMENT,
  CHANGE_REQUEST_FRAGMENT,
  ChangeRequestStatus,
  Node,
  PERSON_FRAGMENT
} from "@/pages/sections/[sectionId]/change-requests/queries";
import { SECTION_FRAGMENT } from "@/pages/sections/[sectionId]/queries";

export type DashboardEventNode = EventWithType & {
  id: string;
  slug: string;
  title: string;
  startsAt: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  registrationCount: number;
  registrationOpenAt?: string | null;
  registrationCloseAt?: string | null;
  draft: boolean;
  sessions?: { startsAt?: string | null; endsAt?: string | null; maxAttendance?: number | null }[] | null;
};

export type DashboardEventsQueryResponse = {
  events: {
    nodes: DashboardEventNode[];
  };
};

export type DashboardEventsQueryVariables = {
  first: number;
  query?: {
    status?: string[];
    sectionIds?: string[];
    dateRange?: {
      from?: string;
      to?: string;
    };
  };
  orderBy?: {
    field: "STARTS_AT" | "ENDS_AT";
    direction: "ASC" | "DESC";
  };
};

export type CareerServicesDashboardWindow =
  | "LAST_7_DAYS"
  | "LAST_14_DAYS"
  | "LAST_30_DAYS"
  | "LAST_60_DAYS"
  | "LAST_6_MONTHS"
  | "LAST_12_MONTHS";

export type JobDashboardQueueRow = {
  id: string;
  name: string | null;
  facilityId: string | null;
  facilityName: string | null;
  sectionId: string | null;
  sectionName: string | null;
  createdAt: string;
  submissionDeadline: string | null;
  closedDate: string | null;
  stage: string | null;
  status: string | null;
  isScrapedJob: boolean;
};

export type JobDashboardQueue = {
  key: string;
  totalCount: number;
  hasMore: boolean;
  rows: JobDashboardQueueRow[];
};

export type JobDashboardMetric = {
  key: string;
  totalCount: number;
};

export type JobDashboardQueuesQueryResponse = {
  jobDashboardQueues: {
    drafts: JobDashboardQueue;
    reviews: JobDashboardQueue;
    expired: JobDashboardQueue;
    metrics: JobDashboardMetric[];
    windowStartAt: string;
    windowEndAt: string;
  };
};

export type JobDashboardQueuesQueryVariables = {
  input: {
    sectionId?: string;
    window?: CareerServicesDashboardWindow;
    previewLimit?: number;
  };
};

export type CareerServicesDashboardSection = {
  id: string;
  name: string;
  legacyId: string | null;
};

export type CareerServicesDashboardSectionsQueryResponse = {
  careerServicesDashboardSections: CareerServicesDashboardSection[];
  careerServicesDashboardSectionSelectionRequired: boolean;
};

export type AssociateDashboardSignalKey =
  | "LEVEL_1_RENEWALS"
  | "LEVEL_2_RENEWALS"
  | "LEVEL_3_RENEWALS"
  | "SUSPENDED_ASSOCIATES";

export type AssociateDashboardSignalRow = {
  id: string;
  pgaId: string | null;
  displayName: string;
  title: string | null;
  facilityName: string | null;
  signalDate: string;
  associateLevel: number | null;
  attentionKind: string;
  suspensionCode: string | null;
  detail: string | null;
};

export type AssociateDashboardSignal = {
  key: AssociateDashboardSignalKey;
  totalCount: number;
  hasMore: boolean;
  rows: AssociateDashboardSignalRow[];
};

export type AssociateDashboardSignalsQueryResponse = {
  associateDashboardSignals: {
    signals: AssociateDashboardSignal[];
    renewalWindowStartDate: string;
    renewalWindowEndDate: string;
    suspensionWindowStartDate: string;
    suspensionWindowEndDate: string;
  };
};

export type AssociateDashboardSignalsQueryVariables = {
  input: {
    sectionId?: string;
    window: CareerServicesDashboardWindow;
    previewLimit: number;
  };
};

export type AssociateDashboardCelebrationKey = "PAT_PASSED" | "LEVEL_COMPLETIONS" | "BIRTHDAYS";

export type AssociateDashboardCelebrationRow = {
  id: string;
  pgaId: string | null;
  displayName: string;
  title: string | null;
  facilityName: string | null;
  celebrationDate: string;
  celebrationKind: string;
  detail: string | null;
};

export type AssociateDashboardCelebration = {
  key: AssociateDashboardCelebrationKey;
  totalCount: number;
  hasMore: boolean;
  rows: AssociateDashboardCelebrationRow[];
};

export type AssociateDashboardCelebrationsQueryResponse = {
  associateDashboardCelebrations: {
    celebrations: AssociateDashboardCelebration[];
    windowStartAt: string;
    windowEndAt: string;
  };
};

export type AssociateDashboardCelebrationsQueryVariables = {
  input: {
    sectionId?: string;
    window: CareerServicesDashboardWindow;
    previewLimit: number;
  };
};

export type AssociateDashboardPacingLevel = {
  key: string;
  label: string;
  completed: number;
  total: number;
  percent: number;
};

export type AssociateDashboardPacingQueryResponse = {
  associateDashboardPacing: {
    levels: AssociateDashboardPacingLevel[];
    asOf: string;
  };
};

export type AssociateDashboardPacingQueryVariables = {
  input: {
    sectionId?: string;
  };
};

export type MemberDashboardPacingKey = "PDR" | "DUES";

export type MemberDashboardPacingStatus = "COMPLETE" | "INCOMPLETE" | "NO_DATA";

export type MemberDashboardPacingMetric = {
  key: MemberDashboardPacingKey;
  label: string;
  completed: number;
  total: number;
  percent: number;
  status: MemberDashboardPacingStatus;
  reason: string;
};

export type MemberDashboardPacingQueryResponse = {
  memberDashboardPacing: {
    metrics: MemberDashboardPacingMetric[];
    asOf: string;
  };
};

export type MemberDashboardPacingQueryVariables = {
  input: {
    sectionId?: string;
    kinds?: MemberDashboardPacingKey[];
  };
};

export type MemberDashboardSignalKey =
  | "RECENTLY_UNEMPLOYED"
  | "UNUSUAL_APPLICATION_ACTIVITY"
  | "SUSPENDED"
  | "NO_INTERACTION";

export type MemberDashboardSignalRow = {
  id: string;
  pgaId: string | null;
  displayName: string;
  title: string | null;
  facilityName: string | null;
  signalDate: string | null;
  applicationCount: number | null;
  suspensionCode: string | null;
};

export type MemberDashboardSignal = {
  key: MemberDashboardSignalKey;
  totalCount: number;
  hasMore: boolean;
  rows: MemberDashboardSignalRow[];
};

export type MemberDashboardSignalsQueryResponse = {
  memberDashboardSignals: {
    signals: MemberDashboardSignal[];
    windowStartAt: string;
    windowEndAt: string;
  };
};

export type MemberDashboardSignalsQueryVariables = {
  input: {
    sectionId?: string;
    window: CareerServicesDashboardWindow;
    previewLimit: number;
  };
};

export type MemberDashboardActivityKey =
  | "EMPLOYMENT_CHANGES"
  | "MEMBERSHIP_CLASS_CHANGES"
  | "COMPENSATION_PROFILE"
  | "CAREER_PROFILE";

export type MemberDashboardActivityRow = {
  id: string;
  pgaId: string | null;
  displayName: string;
  title: string | null;
  facilityName: string | null;
  activityDate: string;
  activityKind: string;
  detail: string | null;
};

export type MemberDashboardActivity = {
  key: MemberDashboardActivityKey;
  totalCount: number;
  hasMore: boolean;
  rows: MemberDashboardActivityRow[];
};

export type MemberDashboardActivityQueryResponse = {
  memberDashboardActivity: {
    activities: MemberDashboardActivity[];
    windowStartAt: string;
    windowEndAt: string;
  };
};

export type MemberDashboardActivityQueryVariables = {
  input: {
    sectionId?: string;
    window: CareerServicesDashboardWindow;
    previewLimit: number;
  };
};

export type MemberDashboardCelebrationKey =
  | "ELECTIONS"
  | "QUARTER_CENTURY"
  | "HALF_CENTURY"
  | "CERTIFICATIONS"
  | "BIRTHDAYS";

export type MemberDashboardCelebrationRow = {
  id: string;
  pgaId: string | null;
  displayName: string;
  title: string | null;
  facilityName: string | null;
  celebrationDate: string;
  celebrationKind: string;
  detail: string | null;
};

export type MemberDashboardCelebration = {
  key: MemberDashboardCelebrationKey;
  totalCount: number;
  hasMore: boolean;
  rows: MemberDashboardCelebrationRow[];
};

export type MemberDashboardCelebrationsQueryResponse = {
  memberDashboardCelebrations: {
    celebrations: MemberDashboardCelebration[];
    windowStartAt: string;
    windowEndAt: string;
  };
};

export type MemberDashboardCelebrationsQueryVariables = {
  input: {
    sectionId?: string;
    window: CareerServicesDashboardWindow;
    previewLimit: number;
  };
};

export const CAREER_SERVICES_DASHBOARD_SECTIONS_QUERY: TypedDocumentNode<CareerServicesDashboardSectionsQueryResponse> = gql`
  query CareerServicesDashboardSections {
    careerServicesDashboardSections {
      id
      name
      legacyId
    }
    careerServicesDashboardSectionSelectionRequired
  }
`;

export const JOB_DASHBOARD_QUEUES_QUERY: TypedDocumentNode<
  JobDashboardQueuesQueryResponse,
  JobDashboardQueuesQueryVariables
> = gql`
  query JobDashboardQueues($input: BackofficeCareerServicesDashboardInput!) {
    jobDashboardQueues(input: $input) {
      drafts {
        key
        totalCount
        hasMore
        rows {
          id
          name
          facilityId
          facilityName
          sectionId
          sectionName
          createdAt
          submissionDeadline
          closedDate
          stage
          status
          isScrapedJob
        }
      }
      reviews {
        key
        totalCount
        hasMore
        rows {
          id
          name
          facilityId
          facilityName
          sectionId
          sectionName
          createdAt
          submissionDeadline
          closedDate
          stage
          status
          isScrapedJob
        }
      }
      expired {
        key
        totalCount
        hasMore
        rows {
          id
          name
          facilityId
          facilityName
          sectionId
          sectionName
          createdAt
          submissionDeadline
          closedDate
          stage
          status
          isScrapedJob
        }
      }
      metrics {
        key
        totalCount
      }
      windowStartAt
      windowEndAt
    }
  }
`;

export const ASSOCIATE_DASHBOARD_SIGNALS_QUERY: TypedDocumentNode<
  AssociateDashboardSignalsQueryResponse,
  AssociateDashboardSignalsQueryVariables
> = gql`
  query AssociateDashboardSignals($input: BackofficeAssociateDashboardSignalsInput!) {
    associateDashboardSignals(input: $input) {
      signals {
        key
        totalCount
        hasMore
        rows {
          id
          pgaId
          displayName
          title
          facilityName
          signalDate
          associateLevel
          attentionKind
          suspensionCode
          detail
        }
      }
      renewalWindowStartDate
      renewalWindowEndDate
      suspensionWindowStartDate
      suspensionWindowEndDate
    }
  }
`;

export const ASSOCIATE_DASHBOARD_CELEBRATIONS_QUERY: TypedDocumentNode<
  AssociateDashboardCelebrationsQueryResponse,
  AssociateDashboardCelebrationsQueryVariables
> = gql`
  query AssociateDashboardCelebrations($input: BackofficeAssociateDashboardCelebrationsInput!) {
    associateDashboardCelebrations(input: $input) {
      celebrations {
        key
        totalCount
        hasMore
        rows {
          id
          pgaId
          displayName
          title
          facilityName
          celebrationDate
          celebrationKind
          detail
        }
      }
      windowStartAt
      windowEndAt
    }
  }
`;

export const ASSOCIATE_DASHBOARD_PACING_QUERY: TypedDocumentNode<
  AssociateDashboardPacingQueryResponse,
  AssociateDashboardPacingQueryVariables
> = gql`
  query AssociateDashboardPacing($input: BackofficeAssociateDashboardPacingInput!) {
    associateDashboardPacing(input: $input) {
      levels {
        key
        label
        completed
        total
        percent
      }
      asOf
    }
  }
`;

export const MEMBER_DASHBOARD_PACING_QUERY: TypedDocumentNode<
  MemberDashboardPacingQueryResponse,
  MemberDashboardPacingQueryVariables
> = gql`
  query MemberDashboardPacing($input: BackofficeMemberDashboardPacingInput!) {
    memberDashboardPacing(input: $input) {
      metrics {
        key
        label
        completed
        total
        percent
        status
        reason
      }
      asOf
    }
  }
`;

export const MEMBER_DASHBOARD_SIGNALS_QUERY: TypedDocumentNode<
  MemberDashboardSignalsQueryResponse,
  MemberDashboardSignalsQueryVariables
> = gql`
  query MemberDashboardSignals($input: BackofficeMemberDashboardSignalsInput!) {
    memberDashboardSignals(input: $input) {
      signals {
        key
        totalCount
        hasMore
        rows {
          id
          pgaId
          displayName
          title
          facilityName
          signalDate
          applicationCount
          suspensionCode
        }
      }
      windowStartAt
      windowEndAt
    }
  }
`;

export const MEMBER_DASHBOARD_ACTIVITY_QUERY: TypedDocumentNode<
  MemberDashboardActivityQueryResponse,
  MemberDashboardActivityQueryVariables
> = gql`
  query MemberDashboardActivity($input: BackofficeMemberDashboardActivityInput!) {
    memberDashboardActivity(input: $input) {
      activities {
        key
        totalCount
        hasMore
        rows {
          id
          pgaId
          displayName
          title
          facilityName
          activityDate
          activityKind
          detail
        }
      }
      windowStartAt
      windowEndAt
    }
  }
`;

export const MEMBER_DASHBOARD_CELEBRATIONS_QUERY: TypedDocumentNode<
  MemberDashboardCelebrationsQueryResponse,
  MemberDashboardCelebrationsQueryVariables
> = gql`
  query MemberDashboardCelebrations($input: BackofficeMemberDashboardCelebrationsInput!) {
    memberDashboardCelebrations(input: $input) {
      celebrations {
        key
        totalCount
        hasMore
        rows {
          id
          pgaId
          displayName
          title
          facilityName
          celebrationDate
          celebrationKind
          detail
        }
      }
      windowStartAt
      windowEndAt
    }
  }
`;

export const DASHBOARD_EVENTS_QUERY: TypedDocumentNode<DashboardEventsQueryResponse, DashboardEventsQueryVariables> =
  gql`
    query DashboardEvents(
      $query: BackofficeEventsSearchInput
      $orderBy: BackofficeEventsEventOrderByInput
      $first: Int
    ) {
      events(query: $query, orderBy: $orderBy, first: $first) {
        nodes {
          id
          slug
          title
          draft
          startsAt
          endsAt
          timezone
          registrationOpenAt
          registrationCloseAt
          registrationCount
          activityType {
            id
            title
          }
          activityTypeSummary {
            id
            title
          }
          eventType {
            id
            name
          }
          eventTypeSummary {
            id
            name
          }
          sessions {
            startsAt
            endsAt
            maxAttendance
          }
        }
      }
    }
  `;

export type RecentInteractionNode = {
  id: string;
  interactionDate: string;
  createdAt: string;
  summary: string;
  relatedTo: string;
  showAiSummary: boolean;
  participants: {
    firstName: string;
    lastName: string;
    profilePhoto: string;
    pgaId: string;
  }[];
  lastMessage: {
    content: string;
    isPrivate: boolean;
    haveAccess: boolean;
  };
};

export type RecentInteractionsQueryResponse = {
  crmInteractions: {
    totalCount: number;
    nodes: RecentInteractionNode[];
  };
};

export type RecentInteractionsInput = {
  authorId?: string;
};

export type RecentInteractionsOrderByInput = {
  field: "CREATED_AT" | "INTERACTION_DATE" | "UPDATED_AT";
  direction: "DESC";
};

export type RecentInteractionsQueryVariables = {
  first: number;
  input?: RecentInteractionsInput;
  orderBy?: RecentInteractionsOrderByInput;
};

export const DASHBOARD_PENDING_CHANGE_REQUEST_LIMIT = 3;

export type DashboardPendingChangeRequestsQueryResponse = {
  section: {
    id: string;
    permissions: {
      manageChangeRequests: boolean;
    };
    changeRequests: {
      totalCount: number;
      nodes: (Node | null)[];
    };
  };
};

export type DashboardPendingChangeRequestsQueryVariables = {
  sectionId: string;
  first: number;
  stage: ChangeRequestStatus.PENDING;
};

export const DASHBOARD_PENDING_CHANGE_REQUESTS_QUERY: TypedDocumentNode<
  DashboardPendingChangeRequestsQueryResponse,
  DashboardPendingChangeRequestsQueryVariables
> = gql`
  query DashboardPendingChangeRequests($sectionId: ID!, $first: Int!, $stage: BackofficeChangeRequestStatus!) {
    section(id: $sectionId) {
      ...SectionFragment
      changeRequests(stage: $stage, first: $first) {
        totalCount
        nodes {
          ...ChangeRequest
          submittedAt
          ...Change
          person {
            pgaId
            ...Person
          }
        }
      }
    }
  }

  ${SECTION_FRAGMENT}
  ${PERSON_FRAGMENT}
  ${CHANGE_REQUEST_FRAGMENT}
  ${CHANGE_REQUEST_CHANGE_FRAGMENT}
`;

export const RECENT_INTERACTIONS_QUERY: TypedDocumentNode<
  RecentInteractionsQueryResponse,
  RecentInteractionsQueryVariables
> = gql`
  query RecentInteractions(
    $first: Int
    $input: BackofficeCRMInteractionsInput
    $orderBy: BackofficeCRMInteractionOrderByInput
  ) {
    crmInteractions(first: $first, input: $input, orderBy: $orderBy) {
      totalCount
      nodes {
        id
        interactionDate
        createdAt
        summary
        relatedTo
        showAiSummary
        participants {
          firstName
          lastName
          profilePhoto
          pgaId
        }
        lastMessage {
          content
          isPrivate
          haveAccess
        }
      }
    }
  }
`;
